import { router, useForm, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { adminInstrumentTagsPath } from '@/shared/lib/routes'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { INSTRUMENT_TAG_MAX_NAME_LENGTH } from '@/shared/constants/instrumentTag'

interface FormErrors {
  [key: string]: string[] | undefined
}

const FieldError = ({ errors, field }: { errors: FormErrors; field: string }) => {
  const messages = errors[field]
  if (!messages?.length) return null
  return <p className="text-sm text-destructive mt-1">{messages[0]}</p>
}

const InstrumentTagForm = () => {
  const { errors: pageErrors } = usePage<{ errors?: FormErrors }>().props

  useBreadcrumbs([
    { label: t('nav.administration') },
    { label: t('nav.instrumentTags'), href: adminInstrumentTagsPath() },
    { label: t('admin.instrumentTags.newTitle') },
  ])

  const form = useForm({ name: '' })
  const canCreate = useCan(Resources.ADMIN_INSTRUMENT_TAG, Actions.CREATE)

  const errors: FormErrors = (pageErrors as FormErrors | undefined) ?? {}

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({ tag: data }))
    form.post(adminInstrumentTagsPath())
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-6">
        <h1 className="text-2xl font-bold mb-6">{t('admin.instrumentTags.newTitle')}</h1>

        {errors.base && (
          <div className="mb-4 rounded border border-destructive bg-destructive/10 px-4 py-3">
            {errors.base.map((message, index) => (
              <p key={index} className="text-sm text-destructive">
                {message}
              </p>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
          <div className="space-y-1.5">
            <Label htmlFor="name">{t('admin.instrumentTags.nameField')} *</Label>
            <Input
              id="name"
              value={form.data.name}
              onChange={(e) => form.setData('name', e.target.value)}
              maxLength={INSTRUMENT_TAG_MAX_NAME_LENGTH}
              required
            />
            <FieldError errors={errors} field="name" />
          </div>

          {!canCreate && <NoAccessNotice />}

          <div className="flex gap-3 pt-2">
            {canCreate && (
              <Button type="submit" disabled={form.processing}>
                {t('admin.instrumentTags.submitCreate')}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => router.visit(adminInstrumentTagsPath())}
            >
              {t('admin.instrumentTags.cancelButton')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default InstrumentTagForm
